// @ai-generated 2026-06-22T12:01:20+00:00 by malte.ulfers@tngtech.com using claude-sonnet-4-6

import { type ApiAttachment, EnumAttachmentUploadStatus } from '@Model';
import {
    Button,
    FileDropzone,
    type FileRejection,
    FileRejectionErrorCode,
    Modal,
    useToasts,
} from '@synqo/frontend-component-library';
import { IconDownload, IconTrash, IconFileImport } from '@tabler/icons-react';
import { type FC, useCallback } from 'react';
import classes from './AttachmentsModal.module.css';
import { formatFileSize } from '@utils/formatFileSize';

interface AttachmentsModalBaseProps {
    isOpen: boolean;
    onClose: () => void;

    attachments: ApiAttachment[];
    /** Disable the per-row download action. Per-row downloads are additionally locked while an attachment is still uploading or processing. */
    canDownload: boolean;
    onDownload: (id: string) => void | Promise<void>;
}

/** Read-only: list + download only. No upload dropzone, no delete action. */
export interface ReadOnlyAttachmentsModalProps extends AttachmentsModalBaseProps {
    mode: 'readonly';
}

/** Editable: adds upload + delete on top of the read-only surface. */
export interface EditableAttachmentsModalProps extends AttachmentsModalBaseProps {
    mode: 'edit';
    /** MIME types the dropzone will accept. Pass the backend's <c>allowed_mime_types</c> verbatim. */
    allowedMimeTypes: string[];
    /** Maximum upload size in bytes — from the backend's <c>max_file_size</c>. */
    maxFileSize: number;
    /** Hide the dropzone when uploads aren't allowed (e.g. parent in a terminal state). */
    canUpload: boolean;
    /** Disable the per-row delete action. Per-row deletes are additionally locked while an attachment is still uploading or processing. */
    canDelete: boolean;
    onUpload: (file: File) => void | Promise<void>;
    onDelete: (id: string) => void | Promise<void>;
    /** Percent (0–100) of this client's in-flight uploads keyed by attachment id; feeds the per-row progress ring. */
    uploadProgress?: Record<string, number>;
}

export type AttachmentsModalProps = ReadOnlyAttachmentsModalProps | EditableAttachmentsModalProps;

const PROCESSING_TOOLTIP = 'Die Datei wird gerade verarbeitet';

const RING_RADIUS = 7;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

// @ai-generated 2026-07-20T11:01:17+00:00 by philipp.kammerer@synqony.com using claude-fable-5
/** Circular upload indicator: spins while <c>percent</c> is unknown, fills as a progress ring once it is. */
const UploadRing: FC<{ percent?: number }> = ({ percent }) => (
    <svg
        width={18}
        height={18}
        viewBox="0 0 18 18"
        className={percent === undefined ? classes.ringSpinning : undefined}
        role="img"
        aria-label="Upload läuft"
    >
        <circle cx="9" cy="9" r={RING_RADIUS} fill="none" stroke="var(--mantine-color-gray-3)" strokeWidth="2" />
        <circle
            cx="9"
            cy="9"
            r={RING_RADIUS}
            fill="none"
            stroke="var(--mantine-color-green-6)"
            strokeWidth="2"
            strokeLinecap="round"
            strokeDasharray={`${(RING_CIRCUMFERENCE * (percent ?? 25)) / 100} ${RING_CIRCUMFERENCE}`}
            transform="rotate(-90 9 9)"
        />
    </svg>
);

const rejectionMessage = (code: FileRejectionErrorCode | string, maxFileSize: number): string => {
    switch (code) {
        case FileRejectionErrorCode.FileInvalidType:
            return 'Dateityp wird nicht akzeptiert.';
        case FileRejectionErrorCode.FileTooLarge:
            return `Datei ist zu groß (max. ${formatFileSize(maxFileSize)}).`;
        case FileRejectionErrorCode.FileTooSmall:
            return 'Datei ist leer.';
        case FileRejectionErrorCode.TooManyFiles:
            return 'Es kann nur eine Datei gleichzeitig hochgeladen werden.';
        default:
            return 'Datei wurde abgelehnt.';
    }
};

// @ai-generated 2026-07-20T11:01:17+00:00 by philipp.kammerer@synqony.com using claude-fable-5
export const AttachmentsModal: FC<AttachmentsModalProps> = (props) => {
    const { isOpen, onClose, attachments, canDownload, onDownload } = props;
    const editable = props.mode === 'edit' ? props : undefined;
    const { pushToast } = useToasts();

    const maxFileSize = editable?.maxFileSize ?? 0;
    const onUpload = editable?.onUpload;
    const uploadProgress = editable?.uploadProgress ?? {};

    const handleDrop = useCallback(
        (file: File) => {
            if (!onUpload) return;
            void Promise.resolve(onUpload(file)).catch(() => {});
        },
        [onUpload],
    );

    const handleReject = useCallback(
        (rejections: FileRejection[]) => {
            const codes = new Set(rejections.flatMap((r) => r.errors));
            const message = [...codes].map((code) => rejectionMessage(code, maxFileSize)).join(' ');
            pushToast({ message, variant: 'error' });
        },
        [pushToast, maxFileSize],
    );

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Anhänge">
            <div className={classes.body}>
                {editable?.canUpload && (
                    <FileDropzone
                        multiple={false}
                        accept={editable.allowedMimeTypes}
                        maxFileSize={editable.maxFileSize}
                        onDrop={handleDrop}
                        onReject={handleReject}
                    />
                )}

                {attachments.length === 0 ? (
                    <div className={classes.empty}>Keine Anhänge vorhanden.</div>
                ) : (
                    <div className={classes.list}>
                        {attachments.map((a) => {
                            const isActionable = a.upload_status === EnumAttachmentUploadStatus.Ready;
                            return (
                                <div key={a.id} className={classes.row}>
                                    <div className={classes.spinnerCell}>
                                        {a.upload_status === EnumAttachmentUploadStatus.Uploading && (
                                            <UploadRing percent={uploadProgress[a.id]} />
                                        )}
                                        {a.upload_status === EnumAttachmentUploadStatus.Processing && (
                                            <IconFileImport size={18} />
                                        )}
                                    </div>
                                    <div className={classes.fileName} title={a.file_name}>
                                        {a.file_name}
                                    </div>
                                    <Button
                                        onClick={() => {
                                            void Promise.resolve(onDownload(a.id)).catch(() => {});
                                        }}
                                        appearance="transparent"
                                        locked={!canDownload || !isActionable}
                                        tooltip={!isActionable ? PROCESSING_TOOLTIP : 'Herunterladen'}
                                        label="Herunterladen"
                                    >
                                        <IconDownload size={18} />
                                    </Button>
                                    {editable && (
                                        <Button
                                            onClick={() => {
                                                void Promise.resolve(editable.onDelete(a.id)).catch(() => {});
                                            }}
                                            appearance="transparent"
                                            variant="warning"
                                            locked={!editable.canDelete || !isActionable}
                                            tooltip={!isActionable ? PROCESSING_TOOLTIP : 'Löschen'}
                                            label="Löschen"
                                        >
                                            <IconTrash size={18} />
                                        </Button>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </Modal>
    );
};
